import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import getFarmers from "./getFarmers";
import FarmerDrawer from "./FarmerDrawer";
import FarmerFormDrawer from "./FarmerFormDrawer";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch, faPlus, faDownload, faEye, faPen, faPhone,
  faUsers, faUserCheck, faUserSlash, faStar, faMedal,
  faLeaf, faMapMarkerAlt, faSyncAlt, faChevronDown,
  faChevronUp, faSort, faSortUp, faSortDown,
  faToggleOn, faLayerGroup, faSeedling, faBuilding, faGlobe,
} from "@fortawesome/free-solid-svg-icons";
// ─── Color palette ────────────────────────────────────────────────────────────
const C = {
  dark: "#1a3328", mid: "#2d5a3d", bright: "#3d8c5e",
  light: "#e8f5ee", page: "#f4f6f5", border: "#d8e8dc",
  white: "#ffffff", textDk: "#1a2e22", textGr: "#6c757d",
  gold: "#b8860b", goldBg: "#fff8e1",
  silver: "#607d8b", silverBg: "#eceff1",
  red: "#c62828", redBg: "#fdecea",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const initials = (name = "") =>
  name.trim().split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "??";

const bestTs = (f) => f.updatedAt || f.updated_at || f.createdAt || f.created_at || null;

const isActive = (farmer) => {
  const ts = bestTs(farmer);
  return ts && moment(ts).isAfter(moment().subtract(1, "year"));
};

const getCategory = (f) => {
  if (f.farmerCategory) return f.farmerCategory;
  if (f.category) return f.category;
  if (f.existingCustomer === 'Yes') return 'Gold';
  if (parseInt(f.issueCount || 0) > 0) return 'Silver';
  return 'Raw';
};

const getcrops = (f) => {
  const src = f.currentCropsCultivated || f.previousCropsCultivated;
  if (!src) return [];
  if (Array.isArray(src)) return src.map((c) => String(c).trim()).filter(Boolean);
  return String(src).split(",").map((c) => c.trim()).filter(Boolean);
};

// ─── Avatar colours (cycle by index) ─────────────────────────────────────────
const AVATAR_COLORS = ["#2d5a3d","#3b82f6","#d4a853","#7b1fa2","#b76a00","#c62828","#2d7a45","#1e6fc4"];

// ─── Sub-components ───────────────────────────────────────────────────────────
const Avatar = ({ name, idx }) => (
  <div style={{
    width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
    backgroundColor: AVATAR_COLORS[idx % AVATAR_COLORS.length],
    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "0.78rem", fontWeight: 700,
  }}>
    {initials(name)}
  </div>
);

const CategoryBadge = ({ cat }) => {
  const cfg = {
    Gold:   { bg: C.goldBg,   text: C.gold,   icon: faStar  },
    Silver: { bg: C.silverBg, text: C.silver,  icon: faMedal },
    Raw:    { bg: C.light,    text: C.mid,     icon: faLeaf  },
  }[cat] || { bg: C.light, text: C.mid, icon: faLeaf };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: "0.68rem", fontWeight: 700, padding: "3px 8px", borderRadius: 10,
      backgroundColor: cfg.bg, color: cfg.text }}>
      <FontAwesomeIcon icon={cfg.icon} style={{ fontSize: "0.6rem" }} />{cat}
    </span>
  );
};

const StatusBadge = ({ active }) => (
  <span style={{
    fontSize: "0.68rem", fontWeight: 700, padding: "3px 8px", borderRadius: 10,
    backgroundColor: active ? C.light : C.redBg,
    color: active ? C.bright : C.red,
  }}>
    {active ? "Active" : "Inactive"}
  </span>
);

// Crop colour palette — cycles for unknown crops
const CROP_COLORS = [
  { bg: "#e6f4ea", text: "#2d6a4f" },
  { bg: "#fff3e0", text: "#b76a00" },
  { bg: "#e3f2fd", text: "#1565c0" },
  { bg: "#fce4ec", text: "#ad1457" },
  { bg: "#f3e5f5", text: "#7b1fa2" },
  { bg: "#e8eaf6", text: "#3949ab" },
  { bg: "#e0f7fa", text: "#00838f" },
  { bg: "#f9fbe7", text: "#827717" },
];
const cropColorIdx = {};
let _cropColorCounter = 0;
const getCropColor = (crop) => {
  if (!cropColorIdx[crop]) cropColorIdx[crop] = ++_cropColorCounter;
  return CROP_COLORS[(cropColorIdx[crop] - 1) % CROP_COLORS.length];
};

const CropChip = ({ crop }) => {
  const { bg, text } = getCropColor(crop);
  return (
    <span style={{
      fontSize: "0.65rem", fontWeight: 600, padding: "2px 8px", borderRadius: 10,
      backgroundColor: bg, color: text, whiteSpace: "nowrap",
    }}>{crop}</span>
  );
};

const StatCard = ({ icon, label, value, color, bg }) => (
  <div style={{
    backgroundColor: C.white, borderRadius: 8, padding: "0.5rem 0.65rem",
    border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: "0.5rem",
    boxShadow: "0 1px 4px rgba(20,50,30,0.05)",
  }}>
    <div style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: bg || C.light,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <FontAwesomeIcon icon={icon} style={{ color: color || C.mid, fontSize: "0.72rem" }} />
    </div>
    <div>
      <div style={{ fontSize: "1.1rem", fontWeight: 800, color: C.textDk, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: "0.62rem", color: C.textGr, marginTop: 1 }}>{label}</div>
    </div>
  </div>
);

// ─── Sidebar filter section ───────────────────────────────────────────────────
const SidebarSection = ({ title, icon, items, selected, onSelect, multiSelect }) => {
  const [open, setOpen] = useState(true);
  // multiSelect: selected is an array; onSelect(value) toggles the value in/out
  const isActive = (value) => multiSelect ? (selected || []).includes(value) : selected === value;
  const handleClick = (value) => {
    if (multiSelect) {
      onSelect(value);  // caller handles toggle
    } else {
      onSelect(isActive(value) ? null : value);
    }
  };
  return (
    <div style={{ marginBottom: "0.75rem" }}>
      {/* Section header */}
      <button onClick={() => setOpen((o) => !o)} style={{
        width: "100%", background: "none",
        borderTop: "none", borderRight: "none", borderBottom: "none", borderLeft: "none",
        outline: "none", padding: "0.25rem 0.4rem 0.4rem",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        cursor: "pointer",
      }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6,
          fontSize: "0.65rem", fontWeight: 800, color: C.textGr,
          letterSpacing: "0.1em", textTransform: "uppercase" }}>
          {icon && (
            <span style={{
              width: 16, height: 16, borderRadius: 4,
              backgroundColor: C.mid,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <FontAwesomeIcon icon={icon} style={{ fontSize: "0.52rem", color: "#fff" }} />
            </span>
          )}
          {title}
        </span>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown}
          style={{ fontSize: "0.55rem", color: C.textGr }} />
      </button>

      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {items.map(({ label, count, value }) => {
            const active = isActive(value);
            return (
              <button
                key={value}
                onClick={() => handleClick(value)}
                style={{
                  width: "100%",
                  borderTop: "none", borderRight: "none", borderBottom: "none", borderLeft: "none",
                  outline: "none", borderRadius: 8,
                  padding: "0.42rem 0.7rem",
                  background: active ? C.mid : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  cursor: "pointer", transition: "background 0.15s",
                  boxShadow: "none",
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = C.light; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {multiSelect && (
                    <span style={{
                      width: 13, height: 13, borderRadius: 3, flexShrink: 0,
                      border: `2px solid ${active ? "#fff" : C.border}`,
                      backgroundColor: active ? "#fff" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {active && <span style={{ width: 7, height: 7, borderRadius: 1, backgroundColor: C.mid }} />}
                    </span>
                  )}
                  <span style={{
                    fontSize: "0.8rem", fontWeight: active ? 700 : 500,
                    color: active ? "#fff" : C.textDk,
                  }}>{label}</span>
                </span>
                <span style={{
                  fontSize: "0.65rem", fontWeight: 700,
                  padding: "1px 7px", borderRadius: 10, minWidth: 22, textAlign: "center",
                  backgroundColor: "#d8f3dc",
                  color: "#1b5e35",
                }}>{count}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Sort header cell ─────────────────────────────────────────────────────────
const TH = ({ label, field, sortField, sortDir, onSort }) => {
  const active = sortField === field;
  return (
    <th onClick={() => onSort(field)} style={{
      padding: "0.6rem 0.75rem", fontSize: "0.7rem", fontWeight: 700,
      color: C.textGr, textTransform: "uppercase", letterSpacing: "0.06em",
      whiteSpace: "nowrap", cursor: field ? "pointer" : "default",
      userSelect: "none", backgroundColor: "#f8faf9",
      borderBottom: `2px solid ${C.border}`, textAlign: "left",
    }}>
      {label}
      {field && (
        <FontAwesomeIcon
          icon={active ? (sortDir === "asc" ? faSortUp : faSortDown) : faSort}
          style={{ marginLeft: 4, fontSize: "0.6rem", opacity: active ? 1 : 0.35 }}
        />
      )}
    </th>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
// ─── Page size ────────────────────────────────────────────────────────────────
// Change here or promote to a user-selectable dropdown later.
const PAGE_SIZE = 20;

export default function Farmer() {
  // ── View drawer ────────────────────────────────────────────────────────────
  const [selectedFarmer, setSelectedFarmer] = useState(null);

  // ── Form drawer (add / edit) ────────────────────────────────────────────────
  // null = closed | { mode: 'add' } | { mode: 'edit', farmer: {...} }
  const [formMode, setFormMode] = useState(null);

  // ── Layer 1: aggregated counts for sidebar + stat cards ─────────────────
  const [aggregates, setAggregates] = useState({
    total: 0, active: 0, inactive: 0,
    topCrops: [], topStates: [], topDistricts: [],
  });
  const [categoryDist,  setCategoryDist]  = useState({ gold: 0, silver: 0, raw: 0 });
  const [statsLoading,  setStatsLoading]  = useState(true);
  const [lastUpdated,   setLastUpdated]   = useState(null);

  // ── Layer 2: server-side paginated table rows ─────────────────────────────
  const [tableRows,    setTableRows]    = useState([]);
  const [total,        setTotal]        = useState(0);
  const [tableLoading, setTableLoading] = useState(false);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);

  // ── Filters ────────────────────────────────────────────────────────────────
  const [search,         setSearch]         = useState("");
  const [filterStatus,   setFilterStatus]   = useState(null);
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterCrop,     setFilterCrop]     = useState([]);
  const [filterDistrict, setFilterDistrict] = useState([]);
  const [filterState,    setFilterState]    = useState([]);

  // ── Sort ───────────────────────────────────────────────────────────────────
  const [sortField, setSortField] = useState("updatedAt");
  const [sortDir,   setSortDir]   = useState("desc");

  // ── Layer 1: fetch aggregated counts (fast — no full record fetch) ──────────
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    const [aggrRes, distRes] = await Promise.all([
      axios.get(`${process.env.REACT_APP_API_URL}/api/farmer/aggregates`).catch(() => ({ data: {} })),
      axios.get(`${process.env.REACT_APP_API_URL}/api/dashboard/farmerCategoryDistribution`).catch(() => ({ data: {} })),
    ]);
    const ag = aggrRes.data || {};
    setAggregates({
      total:        ag.total        || 0,
      active:       ag.active       || 0,
      inactive:     ag.inactive     || 0,
      topCrops:     ag.topCrops     || [],
      topStates:    ag.topStates    || [],
      topDistricts: ag.topDistricts || [],
    });
    const d = distRes.data || {};
    setCategoryDist({ gold: d.gold || 0, silver: d.silver || 0, raw: d.raw || 0 });
    setLastUpdated(moment());
    setStatsLoading(false);
  }, []);

  // ── Layer 2: server-side paginated table ──────────────────────────────────
  const loadTable = useCallback(async (params) => {
    setTableLoading(true);
    const result = await getFarmers(params);
    setTableRows(result.data);
    setTotal(result.total);
    setTableLoading(false);
  }, []);

  const buildParams = useCallback((overrides = {}) => {
    const p = {
      page, limit: PAGE_SIZE,
      search, sortField, sortDir,
      filterCategory, filterCrop, filterDistrict, filterState,
      ...overrides,
    };
    if (p.filterStatus === 'all' || !p.filterStatus) {
      delete p.filterStatus;
    } else {
      p.filterStatus = filterStatus;
      if (overrides.filterStatus !== undefined) p.filterStatus = overrides.filterStatus;
    }
    return p;
  }, [page, search, sortField, sortDir, filterCategory, filterCrop, filterDistrict, filterState, filterStatus]);

  // Fire immediately when non-search params change
  useEffect(() => {
    loadTable(buildParams());
  }, [page, sortField, sortDir, filterStatus, filterCategory, filterCrop, filterDistrict, filterState]); // eslint-disable-line

  // Fire with debounce when search changes
  const searchTimeout = useRef(null);
  const prevSearch = useRef(search);
  useEffect(() => {
    if (search === prevSearch.current) return;
    prevSearch.current = search;
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      loadTable(buildParams({ search, page: 1 }));
    }, 400);
    return () => clearTimeout(searchTimeout.current);
  }, [search]); // eslint-disable-line

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => { loadStats(); }, [loadStats]);

  // ── Sidebar counts (from aggregates API — no full dataset needed) ───────────
  const counts = useMemo(() => ({
    active:    { length: aggregates.active },
    inactive:  { length: aggregates.inactive },
    byCrop:     Object.fromEntries(aggregates.topCrops.map(({ crop, count }) => [crop, count])),
    byState:    Object.fromEntries(aggregates.topStates.map(({ state, count }) => [state, count])),
    byDistrict: Object.fromEntries(aggregates.topDistricts.map(({ district, count }) => [district, count])),
  }), [aggregates]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (!field) return;
    if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
    setPage(1);
  };

  const applyFilter = (setter) => (val) => { setter(val); setPage(1); };

  const makeToggle = (setter) => (val) => {
    setter((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
    setPage(1);
  };
  const toggleCategory = makeToggle(setFilterCategory);
  const toggleCrop     = makeToggle(setFilterCrop);
  const toggleDistrict = makeToggle(setFilterDistrict);
  const toggleState    = makeToggle(setFilterState);

  const refresh = () => { loadStats(); };

  const exportCSV = async () => {
    // Fetch all matching rows server-side (same filters, no page limit)
    const result = await getFarmers(buildParams({ page: 1, limit: 100000 }));
    const rows = result.data;

    const csv = [
      ["Name","Mobile","Village","District","State","Category","Crops","Status","Last Updated"],
      ...rows.map((f) => [
        f.fullName || "", f.mobileNumber || "", f.townVillage || "",
        f.district || "", f.state || "", getCategory(f),
        getcrops(f).join("; "), isActive(f) ? "Active" : "Inactive",
        f.updatedAt ? moment(f.updatedAt).format("DD MMM YYYY") : "",
      ]),
    ].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a"); a.href = url; a.download = "farmers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Sidebar items (pre-sorted by aggregates API, take top 5) ─────────────
  const topCrops     = Object.entries(counts.byCrop).slice(0, 5);
  const topDistricts = Object.entries(counts.byDistrict).slice(0, 5);
  const topStates    = Object.entries(counts.byState).slice(0, 5);

  const loading = statsLoading;   // initial full-page loader

  return (
    <>
    <div style={{ display: "flex", minHeight: "calc(100vh - 64px)", backgroundColor: C.page }}>

      {/* ── Left Sidebar ─────────────────────────────────────────────────── */}
      <div style={{
        width: 220, flexShrink: 0, backgroundColor: C.white,
        borderRight: `1px solid ${C.border}`, padding: "1.25rem 0.9rem",
        overflowY: "auto",
      }}>
        <div style={{ fontSize: "0.7rem", fontWeight: 800, color: C.textGr,
          letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "1rem",
          display: "flex", alignItems: "center", gap: 7 }}>
          <FontAwesomeIcon icon={faSearch} style={{ fontSize: "0.65rem", color: C.mid }} />
          Farmer Filters
        </div>

        <SidebarSection
          title="Status"
          icon={faToggleOn}
          selected={filterStatus}
          onSelect={applyFilter(setFilterStatus)}
          items={[
            { label: "All Farmers", value: "all",      count: aggregates.total },
            { label: "Active",      value: "active",   count: aggregates.active },
            { label: "Inactive",    value: "inactive", count: aggregates.inactive },
          ]}
        />
        <div style={{ height: 1, backgroundColor: C.border, margin: "0.5rem 0 1rem" }} />

        <SidebarSection
          title="Category"
          icon={faLayerGroup}
          selected={filterCategory}
          onSelect={toggleCategory}
          multiSelect
          items={[
            { label: "Gold Farmers",   value: "Gold",   count: categoryDist.gold   },
            { label: "Silver Farmers", value: "Silver", count: categoryDist.silver },
            { label: "Raw Farmers",    value: "Raw",    count: categoryDist.raw    },
          ]}
        />
        <div style={{ height: 1, backgroundColor: C.border, margin: "0.5rem 0 1rem" }} />

        {topCrops.length > 0 && (
          <>
            <SidebarSection
              title="Crops"
              icon={faSeedling}
              selected={filterCrop}
              onSelect={toggleCrop}
              multiSelect
              items={topCrops.map(([c, n]) => ({ label: c, value: c, count: n }))}
            />
            <div style={{ height: 1, backgroundColor: C.border, margin: "0.5rem 0 1rem" }} />
          </>
        )}

        {topStates.length > 0 && (
          <>
            <SidebarSection
              title="State"
              icon={faGlobe}
              selected={filterState}
              onSelect={toggleState}
              multiSelect
              items={topStates.map(([s, n]) => ({ label: s, value: s, count: n }))}
            />
            <div style={{ height: 1, backgroundColor: C.border, margin: "0.5rem 0 1rem" }} />
          </>
        )}

        {topDistricts.length > 0 && (
          <SidebarSection
            title="District"
            icon={faBuilding}
            selected={filterDistrict}
            onSelect={toggleDistrict}
            multiSelect
            items={topDistricts.map(([d, n]) => ({ label: d, value: d, count: n }))}
          />
        )}
      </div>

      {/* ── Main Content ─────────────────────────────────────────────────── */}
      <div style={{ flex: 1, padding: "1.5rem", overflowX: "auto" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1.25rem" }}>
          <div>
            <h4 style={{ fontWeight: 800, color: C.textDk, margin: 0 }}>Farmer Management</h4>
            <div style={{ fontSize: "0.78rem", color: C.textGr, marginTop: 3 }}>
              📍 {filterState.length > 0 ? filterState.join(", ") : "All Regions"} · Last updated{" "}
              {lastUpdated ? lastUpdated.fromNow() : "—"}
              <button onClick={refresh} style={{
                background: "none", border: "none", color: C.bright,
                cursor: "pointer", marginLeft: 6, padding: 0, fontSize: "0.75rem",
              }}>
                <FontAwesomeIcon icon={faSyncAlt} />
              </button>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={exportCSV} style={{
              display: "flex", alignItems: "center", gap: 6,
              backgroundColor: C.white, border: `1px solid ${C.border}`,
              borderRadius: 8, padding: "0.45rem 0.9rem",
              fontSize: "0.82rem", fontWeight: 600, color: C.textDk, cursor: "pointer",
            }}>
              <FontAwesomeIcon icon={faDownload} style={{ fontSize: "0.75rem" }} />Export
            </button>
            <button
              onClick={() => setFormMode({ mode: "add" })}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                backgroundColor: C.mid, border: "none", borderRadius: 8,
                padding: "0.45rem 0.9rem", fontSize: "0.82rem", fontWeight: 600,
                color: "#fff", cursor: "pointer",
              }}>
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: "0.75rem" }} />Add Farmer
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "0.5rem", marginBottom: "1rem" }}>
          <StatCard icon={faUsers}     label="All Farmers"    value={aggregates.total}         color={C.mid}    bg={C.light}    />
          <StatCard icon={faUserCheck} label="Active"         value={aggregates.active}        color="#2e7d32"  bg="#e8f5e9"    />
          <StatCard icon={faUserSlash} label="Inactive"       value={aggregates.inactive}      color={C.red}    bg={C.redBg}   />
          <StatCard icon={faStar}      label="Gold Farmers"   value={categoryDist.gold}        color={C.gold}   bg={C.goldBg}  />
          <StatCard icon={faMedal}     label="Silver Farmers" value={categoryDist.silver}      color={C.silver} bg={C.silverBg}/>
          <StatCard icon={faLeaf}      label="Raw Farmers"    value={categoryDist.raw}         color={C.mid}    bg={C.light}   />
        </div>

        {/* Search + filter bar */}
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
            <FontAwesomeIcon icon={faSearch} style={{
              position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
              color: C.mid, fontSize: "0.8rem",
            }} />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by name, phone, village, district…"
              style={{
                width: "100%", paddingLeft: 30, paddingRight: 10,
                height: 36, borderRadius: 20, border: `1.5px solid ${C.border}`,
                fontSize: "0.82rem", outline: "none", backgroundColor: C.light,
                color: C.textDk,
              }}
            />
          </div>
          {[
            { label: "All Districts", arr: filterDistrict, set: setFilterDistrict, opts: Object.keys(counts.byDistrict) },
            { label: "All Crops",     arr: filterCrop,     set: setFilterCrop,     opts: Object.keys(counts.byCrop)     },
          ].map(({ label, arr, set, opts }) => (
            <select
              key={label}
              value={arr.length === 1 ? arr[0] : ""}
              onChange={(e) => { const v = e.target.value; set(v ? [v] : []); setPage(1); }}
              style={{
                height: 36, borderRadius: 20,
                border: `1.5px solid ${C.border}`,
                padding: "0 0.75rem", fontSize: "0.8rem",
                backgroundColor: C.light,
                color: arr.length > 0 ? C.mid : C.textGr,
                fontWeight: arr.length > 0 ? 600 : 400,
                outline: "none", cursor: "pointer",
              }}
            >
              <option value="">{label}</option>
              {opts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
          <select value={`${sortField}:${sortDir}`} onChange={(e) => {
            const [f, d] = e.target.value.split(":");
            setSortField(f); setSortDir(d); setPage(1);
          }} style={{
            height: 36, borderRadius: 20,
            border: `1.5px solid ${C.border}`,
            padding: "0 0.75rem", fontSize: "0.8rem",
            backgroundColor: C.light, color: C.mid,
            fontWeight: 600, outline: "none", cursor: "pointer",
          }}>
            <option value="updatedAt:desc">Latest First</option>
            <option value="updatedAt:asc">Oldest First</option>
            <option value="fullName:asc">Name A → Z</option>
            <option value="fullName:desc">Name Z → A</option>
          </select>
        </div>

        {/* Table */}
        <div style={{ backgroundColor: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
          boxShadow: "0 4px 16px rgba(20,50,30,0.06)", overflow: "hidden" }}>

          <div style={{ padding: "0.75rem 1rem", borderBottom: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            backgroundColor: C.dark }}>
            <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff" }}>
              Recent &amp; Updated Farmers
            </span>
            <span style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.65)" }}>
              {total} farmer{total !== 1 ? "s" : ""}
            </span>
          </div>

          {loading || tableLoading ? (
            <div style={{ textAlign: "center", padding: "3rem", color: C.textGr }}>
              {loading ? "Loading farmers…" : "Applying filters…"}
            </div>
          ) : tableRows.length === 0 ? (
            <div style={{ textAlign: "center", padding: "3rem", color: C.textGr }}>
              No farmers match the current filters.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <TH label="Farmer"       field="fullName"  sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <TH label="Location"     field={null}      sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <TH label="Farm Details" field={null}      sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <TH label="Crops"        field={null}      sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <TH label="Category"     field={null}      sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <TH label="Status"       field={null}      sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <TH label="Last Updated" field="updatedAt" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <TH label="Actions"      field={null}      sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((f, i) => {
                    const active = isActive(f);
                    const cat    = getCategory(f);
                    const crops  = getcrops(f);
                    const rowBg  = i % 2 === 0 ? C.white : "#f4faf6";
                    return (
                      <tr key={f._id || i} onClick={() => setSelectedFarmer(f)} style={{ backgroundColor: rowBg, borderBottom: `1px solid ${C.border}`, cursor: "pointer" }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#d6eddf"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = rowBg; }}>

                        {/* Farmer */}
                        <td style={{ padding: "0.7rem 0.75rem", minWidth: 160 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                            <Avatar name={f.fullName} idx={(page - 1) * PAGE_SIZE + i} />
                            <div>
                              <div style={{ fontWeight: 700, fontSize: "0.83rem", color: C.textDk,
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }}>
                                {f.fullName || "—"}
                              </div>
                              <div style={{ fontSize: "0.7rem", color: C.textGr }}>{f.mobileNumber || "—"}</div>
                            </div>
                          </div>
                        </td>

                        {/* Location */}
                        <td style={{ padding: "0.7rem 0.75rem", minWidth: 120 }}>
                          <div style={{ fontSize: "0.8rem", fontWeight: 500, color: C.textDk }}>{f.townVillage || "—"}</div>
                          <div style={{ fontSize: "0.7rem", color: C.textGr }}>{f.district || ""}{f.district && f.state ? ", " : ""}{f.state || ""}</div>
                        </td>

                        {/* Farm Details */}
                        <td style={{ padding: "0.7rem 0.75rem", minWidth: 100 }}>
                          {f.landAcreage || f.irrigFacilities ? (
                            <>
                              {f.landAcreage && <div style={{ fontSize: "0.8rem", fontWeight: 600, color: C.textDk }}>{f.landAcreage} Acres</div>}
                              {f.irrigFacilities && <div style={{ fontSize: "0.7rem", color: C.textGr }}>{f.irrigFacilities}</div>}
                            </>
                          ) : (
                            <span style={{ fontSize: "0.75rem", color: C.textGr }}>—</span>
                          )}
                        </td>

                        {/* Crops */}
                        <td style={{ padding: "0.7rem 0.75rem", minWidth: 120 }}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                            {crops.length ? crops.slice(0, 3).map((c) => <CropChip key={c} crop={c} />) : <span style={{ fontSize: "0.75rem", color: C.textGr }}>—</span>}
                            {crops.length > 3 && <span style={{ fontSize: "0.65rem", color: C.textGr }}>+{crops.length - 3}</span>}
                          </div>
                        </td>

                        {/* Category */}
                        <td style={{ padding: "0.7rem 0.75rem" }}>
                          <CategoryBadge cat={cat} />
                        </td>

                        {/* Status */}
                        <td style={{ padding: "0.7rem 0.75rem" }}>
                          <StatusBadge active={active} />
                        </td>

                        {/* Last Updated */}
                        <td style={{ padding: "0.7rem 0.75rem", minWidth: 110 }}>
                          {(() => {
                            const ts = bestTs(f);
                            if (!ts) return <span style={{ fontSize: "0.75rem", color: C.textGr }}>—</span>;
                            return (
                              <>
                                <div style={{ fontSize: "0.78rem", fontWeight: 500, color: C.textDk }}>
                                  {moment(ts).format("DD MMM YYYY")}
                                </div>
                                <div style={{ fontSize: "0.68rem", color: C.textGr }}>
                                  {moment(ts).format("hh:mm A")}
                                </div>
                              </>
                            );
                          })()}
                        </td>

                        {/* Actions */}
                        <td style={{ padding: "0.7rem 0.75rem" }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: "flex", gap: 5 }}>
                            <button
                              title="View Details"
                              onClick={() => setSelectedFarmer(f)}
                              style={actionBtn("#e8f5ee", C.mid)}
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            <button
                              title="Edit"
                              onClick={(e) => { e.stopPropagation(); setFormMode({ mode: "edit", farmer: f }); }}
                              style={actionBtn("#e8f0ff", "#3b82f6")}
                            >
                              ✏️
                            </button>
                            <a href={`tel:${f.mobileNumber}`} onClick={(e) => e.stopPropagation()}>
                              <button title="Call" style={actionBtn("#fff8e1", C.gold)}>
                                📞
                              </button>
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && !tableLoading && total > 0 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "0.75rem 1rem", borderTop: `1px solid ${C.border}`, backgroundColor: "#fafcfb" }}>
              <span style={{ fontSize: "0.75rem", color: C.textGr }}>
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total} farmers
              </span>
              <div style={{ display: "flex", gap: 4 }}>
                <PagBtn label="‹ Prev"  disabled={page === 1}          onClick={() => setPage((p) => p - 1)} />
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pg = page <= 3 ? i + 1 : page - 2 + i;
                  if (pg < 1 || pg > totalPages) return null;
                  return (
                    <PagBtn key={pg} label={pg} active={pg === page} onClick={() => setPage(pg)} />
                  );
                })}
                <PagBtn label="Next ›" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* ── Farmer Detail Drawer ── */}
    {selectedFarmer && (
      <FarmerDrawer
        farmer={selectedFarmer}
        allFarmers={tableRows}
        onClose={() => setSelectedFarmer(null)}
        onFarmerUpdated={(updatedFarmer) => {
          if (updatedFarmer?.mobileNumber) {
            const now = new Date().toISOString();
            setTableRows((prev) => prev.map((f) =>
              f.mobileNumber === updatedFarmer.mobileNumber
                ? { ...f, ...updatedFarmer, _id: f._id, updatedAt: updatedFarmer.updatedAt || now }
                : f
            ));
            setSelectedFarmer((prev) =>
              prev?.mobileNumber === updatedFarmer.mobileNumber
                ? { ...prev, ...updatedFarmer, _id: prev._id, updatedAt: updatedFarmer.updatedAt || now }
                : prev
            );
          }
          refresh();
        }}
      />
    )}

    {/* ── Add / Edit Form Drawer ── */}
    {formMode && (
      <FarmerFormDrawer
        mode={formMode.mode}
        farmer={formMode.farmer}
        onClose={() => setFormMode(null)}
        onSuccess={(updatedFarmer) => {
          setFormMode(null);
          if (updatedFarmer?.mobileNumber) {
            setTableRows((prev) => prev.map((f) =>
              f.mobileNumber === updatedFarmer.mobileNumber
                ? { ...f, ...updatedFarmer, _id: f._id, updatedAt: updatedFarmer.updatedAt || new Date().toISOString() }
                : f
            ));
          }
          refresh();
        }}
      />
    )}
    </>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const actionBtn = (bg, color) => ({
  width: 28, height: 28, borderRadius: 6, border: "none",
  backgroundColor: bg, color, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: "0.72rem",
});

const PagBtn = ({ label, onClick, disabled, active }) => (
  <button onClick={onClick} disabled={disabled} style={{
    minWidth: 32, height: 30, borderRadius: 6, border: `1px solid ${C.border}`,
    backgroundColor: active ? C.mid : C.white,
    color: active ? "#fff" : disabled ? C.border : C.textDk,
    fontSize: "0.78rem", fontWeight: active ? 700 : 400,
    cursor: disabled ? "not-allowed" : "pointer", padding: "0 0.5rem",
  }}>
    {label}
  </button>
);
